import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as api from '../../api';
import Slider from '../../Plateforme/Components/Slider';
import { FD100MultiSelectOptions } from '../../Plateforme/Utils/Listings';
import { useTranslation } from 'react-i18next';
import { makeUrlsAndEmailsLinks } from '../../Plateforme/Utils/Format';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 900px;
  max-height: 90vh;
  width: 100%;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #c82333;
  }
`;

const PopupHeader = styled.div`
  padding: 30px 30px 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const PopupTitle = styled.h2`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 1.8rem;
`;

const PopupSubtitle = styled.div`
  color: #666;
  font-size: 14px;
`;

const PopupContent = styled.div`
  padding: 20px 30px 30px;
`;

const SliderWrapper = styled.div`
  margin-bottom: 30px;
  width: 100%;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 15px;
  margin-bottom: 15px;
`;

const DataLabel = styled.div`
  font-weight: 600;
  color: #0000FF;
  font-size: 14px;
`;

const DataValue = styled.div`
  color: #333;
  white-space: pre-wrap;
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const PieceDetailsPopup = ({ pieceId, participationId, onClose }) => {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { i18n } = useTranslation();

  // Get typologie options in the current language
  const tyoplogieEspaceFD100 = FD100MultiSelectOptions.tyoplogieEspaceFD100(i18n.language);
  const typologieObjetFD100 = FD100MultiSelectOptions.typologieObjetFD100(i18n.language);
  const FDCategories = [
    ...tyoplogieEspaceFD100,
    ...typologieObjetFD100,
  ].filter((cat, index, self) => self.findIndex((t) => t.value === cat.value) === index);

  useEffect(() => {
    fetchSubmission();
  }, [participationId, pieceId]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.getFormSubmissionById(participationId);
      setSubmission(response.data);
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError('Erreur lors du chargement de la pièce');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <PopupOverlay onClick={handleOverlayClick}>
        <PopupContainer>
          <CloseButton onClick={onClose}>×</CloseButton>
          <LoadingSpinner>Chargement de la pièce...</LoadingSpinner>
        </PopupContainer>
      </PopupOverlay>
    );
  }

  if (error || !submission) {
    return (
      <PopupOverlay onClick={handleOverlayClick}>
        <PopupContainer>
          <CloseButton onClick={onClose}>×</CloseButton>
          <PopupContent>
            <p style={{ color: 'red', textAlign: 'center' }}>
              {error || 'Pièce non trouvée'}
            </p>
          </PopupContent>
        </PopupContainer>
      </PopupOverlay>
    );
  }

  const { data } = submission;

  // Extract piece index from pieceId (format: "submissionId_pieceIndex")
  let pieceIndex = 0;
  if (pieceId && pieceId.includes('_')) {
    const parts = pieceId.split('_');
    pieceIndex = parseInt(parts[1]) || 0;
  }

  const piece = data.pieces && data.pieces.length > pieceIndex ? data.pieces[pieceIndex] : null;

  if (!piece) {
    return (
      <PopupOverlay onClick={handleOverlayClick}>
        <PopupContainer>
          <CloseButton onClick={onClose}>×</CloseButton>
          <PopupContent>
            <p style={{ color: 'red', textAlign: 'center' }}>
              Aucune pièce trouvée dans cette candidature (index: {pieceIndex})
            </p>
          </PopupContent>
        </PopupContainer>
      </PopupOverlay>
    );
  }

  const medias = piece.uploadedMedia || [];

  // Transform media data for Slider component
  // The Slider expects paths without "userfiles/" as it adds it automatically
  // Milan 2026 media paths are like "milan2026/[id]/[filename]" so we need to adjust
  const slides = medias.map(media => {
    // Remove "userfiles/" if it exists at the beginning of the path
    let adjustedPath = media.path;
    if (adjustedPath.startsWith('userfiles/')) {
      adjustedPath = adjustedPath.replace('userfiles/', '');
    }

    return {
      path: adjustedPath,
      type: media.mimetype || 'image',
      credit: media.credit || '',
      legende: media.originalname || ''
    };
  });

  return (
    <PopupOverlay onClick={handleOverlayClick}>
      <PopupContainer>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <PopupHeader>
          <PopupTitle>{piece.name}</PopupTitle>
          <PopupSubtitle>
            {data.userType === 'designer' ? data.designerCompanyName : data.fabricantCompanyName}
          </PopupSubtitle>
        </PopupHeader>

        <PopupContent>
          {slides.length > 0 && (
            <SliderWrapper>
              <Slider slides={slides} height="400px" />
            </SliderWrapper>
          )}

          <DataGrid>
            <DataLabel>Typologie:</DataLabel>
            <DataValue>
              {piece.typologie === 'autre' && piece.typologieOther
                ? makeUrlsAndEmailsLinks(piece.typologieOther)
                : FDCategories.find(cat => cat.value === piece.typologie)?.label || piece.typologie || '-'}
            </DataValue>

            <DataLabel>Date de création:</DataLabel>
            <DataValue>{makeUrlsAndEmailsLinks(piece.creationDate)}</DataValue>

            <DataLabel>Savoir-faire:</DataLabel>
            <DataValue>{makeUrlsAndEmailsLinks(piece.savoirFaire)}</DataValue>

            <DataLabel>Durabilité:</DataLabel>
            <DataValue>{makeUrlsAndEmailsLinks(piece.durability)}</DataValue>

            <DataLabel>Dimensions:</DataLabel>
            <DataValue>{makeUrlsAndEmailsLinks(piece.dimensions)}</DataValue>

            <DataLabel>Matériaux:</DataLabel>
            <DataValue>{makeUrlsAndEmailsLinks(piece.materials)}</DataValue>

            {piece.poids && (
              <>
                <DataLabel>Poids:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(piece.poids)}</DataValue>
              </>
            )}
          </DataGrid>

          {/* Designer info for the piece */}
          {((data.userType === 'fabricant' && piece.designerCompanyName) ||
            (data.userType === 'designer' && data.designerCompanyName)) && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #d0e8ff' }}>
              <h4 style={{ color: '#0000FF', marginBottom: '12px', fontSize: '1rem', fontWeight: '600' }}>Informations du Designer</h4>
              <DataGrid>
                <DataLabel>Nom de l'entreprise:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(piece.designerCompanyName || data.designerCompanyName)}</DataValue>

                {(piece.designerInternationalPresence || data.designerInternationalPresence) && (
                  <>
                    <DataLabel>Présence internationale:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.designerInternationalPresence || data.designerInternationalPresence)}</DataValue>
                  </>
                )}

                {(piece.designerInternationalDetails || data.designerInternationalDetails) && (
                  <>
                    <DataLabel>Détails international:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.designerInternationalDetails || data.designerInternationalDetails)}</DataValue>
                  </>
                )}

                {(piece.designerWebAndSocial || data.designerWebAndSocial) && (
                  <>
                    <DataLabel>Site web et réseaux sociaux:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.designerWebAndSocial || data.designerWebAndSocial)}</DataValue>
                  </>
                )}

                {(piece.designerWebAndSocialDetails || data.designerWebAndSocialDetails) && (
                  <>
                    <DataLabel>Détails web/social:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.designerWebAndSocialDetails || data.designerWebAndSocialDetails)}</DataValue>
                  </>
                )}

                {(piece.designerPresentation || data.designerPresentation) && (
                  <>
                    <DataLabel>Présentation:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.designerPresentation || data.designerPresentation)}</DataValue>
                  </>
                )}
              </DataGrid>
            </div>
          )}

          {/* Fabricant info for the piece */}
          {((data.userType === 'designer' && piece.fabricantCompanyName) ||
            (data.userType === 'fabricant' && data.fabricantCompanyName)) && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #d0e8ff' }}>
              <h4 style={{ color: '#0000FF', marginBottom: '12px', fontSize: '1rem', fontWeight: '600' }}>Informations du Fabricant/Éditeur</h4>
              <DataGrid>
                <DataLabel>Nom de l'entreprise:</DataLabel>
                <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantCompanyName || data.fabricantCompanyName)}</DataValue>

                {(piece.fabricantCodifab || data.fabricantCodifab) && (
                  <>
                    <DataLabel>Cotisant CODIFAB:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantCodifab || data.fabricantCodifab)}</DataValue>
                  </>
                )}

                {(piece.fabricantAmeublement || data.fabricantAmeublement) && (
                  <>
                    <DataLabel>Adhérent Ameublement français:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantAmeublement || data.fabricantAmeublement)}</DataValue>
                  </>
                )}

                {(piece.fabricantInternationalPresence || data.fabricantInternationalPresence) && (
                  <>
                    <DataLabel>Présence internationale:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantInternationalPresence || data.fabricantInternationalPresence)}</DataValue>
                  </>
                )}

                {(piece.fabricantInternationalDetails || data.fabricantInternationalDetails) && (
                  <>
                    <DataLabel>Détails international:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantInternationalDetails || data.fabricantInternationalDetails)}</DataValue>
                  </>
                )}

                {(piece.fabricantWebAndSocial || data.fabricantWebAndSocial) && (
                  <>
                    <DataLabel>Site web et réseaux sociaux:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantWebAndSocial || data.fabricantWebAndSocial)}</DataValue>
                  </>
                )}

                {(piece.fabricantWebAndSocialDetails || data.fabricantWebAndSocialDetails) && (
                  <>
                    <DataLabel>Détails web/social:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantWebAndSocialDetails || data.fabricantWebAndSocialDetails)}</DataValue>
                  </>
                )}

                {(piece.fabricantPresentation || data.fabricantPresentation) && (
                  <>
                    <DataLabel>Présentation:</DataLabel>
                    <DataValue>{makeUrlsAndEmailsLinks(piece.fabricantPresentation || data.fabricantPresentation)}</DataValue>
                  </>
                )}
              </DataGrid>
            </div>
          )}

          {piece.presentationText && (
            <div style={{ marginTop: '20px' }}>
              <DataLabel style={{ marginBottom: '10px', display: 'block' }}>Présentation:</DataLabel>
              <DataValue style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                {makeUrlsAndEmailsLinks(piece.presentationText)}
              </DataValue>
            </div>
          )}
        </PopupContent>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default PieceDetailsPopup;